import { FormControl, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { finalize, map, Observable, of, Subscription } from 'rxjs';

import { UserCore } from '@reg/shared/model';
import { UserService } from '@reg/shared/service';

@Directive({
    selector: '[floAsyncUserCoreUsernameCheckValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: UserCoreUsernameCheckDirective, multi: true },
    ],
})
export class UserCoreUsernameCheckDirective implements OnDestroy {
    @Output() public isValidatingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public userCoreExistsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() public invalidIfExists: boolean = true;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private timeout: any;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    constructor(private userService: UserService) {
    }

    public validate(control: FormControl): Observable<any> {
        clearTimeout(this.timeout);
        this.isValidating = true;
        this.formControl = control;

        if (!control.value) {
            this.userCoreExistsChange.emit(false);
            this.isValidating = false;
            return of(null);
        }

        // Set a timeout so we don't make an http call for every key stroke.
        return new Observable((obs: any): void => {
            this.timeout = setTimeout(
                () => {
                    this.delay = this.defaultDelay;

                    this.userService.getCheckCoreUserByUsername(control.value)
                        .pipe(
                            map(
                                (exists: boolean) => {
                                    if (!exists ||
                                        !control.value) {
                                        this.userCoreExistsChange.emit(false);
                                        return null;
                                    }

                                    this.userCoreExistsChange.emit(exists);

                                    if (exists &&
                                        this.invalidIfExists) {
                                        return { usernameAlreadyExists: true };
                                    }

                                    return null;
                                },
                            ),
                            finalize(() => {
                                obs.complete();
                                this.isValidating = false;
                            }),
                        ).subscribe({
                            next: (data: any) => { obs.next(data); },
                            error: (error: any) => { obs.next({ invalid: true }); },
                        });
                },
                this.delay,
            );
        });
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }
}
